import type { AxiosError } from "axios";
import { useRef, useState } from "react";
import { useQuery } from "react-query";
import { POLL_BUDGET_MS, POLL_INTERVAL_MS } from "../constants";
import {
  type OrderConfirmationParams,
  OrderConfirmationRepository,
  type OrderConfirmationStatusResponse,
} from "./order-confirmation.api";

const repository = new OrderConfirmationRepository();

export type OrderConfirmationStatus = "loading" | "ready" | "not_found" | "technical_error";

type UseOrderConfirmationStatusOptions = OrderConfirmationParams & {
  enabled: boolean;
};

type ApiErrorBody = { error?: string };

const isNotFoundError = (error: AxiosError | null): boolean => {
  if (!error || error.response?.status !== 404) return false;
  const body = error.response.data as ApiErrorBody | undefined;
  return body?.error === "order_confirmation_not_found";
};

export const useOrderConfirmationStatus = ({ slug, token, enabled }: UseOrderConfirmationStatusOptions) => {
  const startedAtRef = useRef(Date.now());
  const [givenUp, setGivenUp] = useState(false);

  const query = useQuery<OrderConfirmationStatusResponse, AxiosError>({
    queryKey: ["orderConfirmationStatus", slug, token],
    queryFn: () => repository.getStatus({ slug, token }),
    enabled,
    retry: false,
    refetchOnWindowFocus: false,
    useErrorBoundary: false,
    refetchInterval: (data, currentQuery) => {
      if (data !== undefined) return false;
      if (isNotFoundError(currentQuery.state.error ?? null)) return false;
      if (Date.now() - startedAtRef.current >= POLL_BUDGET_MS) {
        queueMicrotask(() => setGivenUp(true));
        return false;
      }

      return POLL_INTERVAL_MS;
    },
  });

  const status: OrderConfirmationStatus = (() => {
    if (query.isSuccess) return "ready";
    if (isNotFoundError(query.error ?? null)) return "not_found";
    if (givenUp) return "technical_error";
    return "loading";
  })();

  return { status };
};
